import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { emailApi, type RequestPasswordResetRequest } from '../api/emailApi';
import { AuthContainer } from '../components/index';
import { Toast } from '../../../shared/components/ui';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const requestResetMutation = useMutation({
    mutationFn: (data: RequestPasswordResetRequest) => emailApi.requestPasswordReset(data),
    onSuccess: (response) => {
      if (response.success) {
        setIsSubmitted(true);
        setError('');
        setToastMessage('Reset link generated successfully! Check your email.');
        setShowToast(true);
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to send reset link';
      setError(message);
      setToastMessage(message);
      setShowToast(true);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Email is required');
      return;
    }

    requestResetMutation.mutate({ email });
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <AuthContainer>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h1>
              <p className="text-gray-600 mb-6">
                We've sent a password reset link to <span className="font-medium text-blue-600">{email}</span>
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try another email
                </button>
                <Link to="/sign-in" className="block text-center text-blue-600 hover:text-blue-700 transition-colors">Back to sign in</Link>
              </div>
            </div>
          </AuthContainer>
        </div>
        {showToast && (
          <Toast
            id="forgot-password-toast-1"
            title="Success"
            message={toastMessage}
            type="success"
            onClose={() => setShowToast(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <AuthContainer>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Forgot password</h1>
            <p className="text-gray-600">Start managing your sober living community with ease.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {error && (
                <div className="mt-1 text-sm text-red-600">
                  {error}
                  {error === 'Email not registered' && (
                    <button
                      type="button"
                      onClick={() => showToastMessage('Redirecting to sign up...')}
                      className="ml-2 text-blue-600 hover:text-blue-700"
                    >
                      Create account?
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={requestResetMutation.isPending}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {requestResetMutation.isPending ? 'Sending...' : 'Send reset link'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/sign-in" className="text-blue-600 hover:text-blue-700 transition-colors">Login account</Link>
          </div>
        </AuthContainer>
      </div>
      {showToast && (
        <Toast
          id="forgot-password-toast-2"
          title={error ? 'Error' : 'Success'}
          message={toastMessage}
          type={error ? 'error' : 'success'}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};
